import React, { ReactNode } from 'react';
import { Box, Text, Button, Flex, Label, Checkbox } from '@ascension/web';
import CodeChipInput from './ProcedureFormFields/CodeChipInput';
import { ProcedureFormProps } from './ProcedureFormProps';
import ImplantsField from './ProcedureFormFields/ImplantsField';
import { useProcedureFormInfo } from './ProcedureFormContext/ProcedureFormContext';
import FormTextArea from '../FormComponents/FormTextArea';
import { StatefulTextInput } from '@components/StatefulInputs/StatefulTextInput/StatefulTextInput';
import FormTextField from '../FormComponents/FormTextField';
import { FormMetaProvider } from '@store/context/FormMetaContext';
import { useFormContext } from 'react-hook-form';
import { SurgeryProcedureInterface } from '../RequestForm/SurgeryInformation/SurgeryInformationInterface';
import { Paper } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { useFieldFormContext } from '@utilities/hooks/useFieldFormContext/useFieldFormContext';
import { EquipmentMultiSelect } from '@components/ProcedureForm/inputs/EquipmentMultiSelect/EquipmentMultiSelect';
import { useSsmUiFlags } from '@utilities/hooks/useSsmUiFlags/useSsmUiFlags';
import { SecondarySurgeonSelect } from '@components/ProcedureForm/inputs/SecondarySurgeonSelect/SecondarySurgeonSelect';
import { SECONDARY_SURGEON_NAME_REQUIRED, SURGEON_NOT_SELECTED } from '@utilities/Validation/ValidationMessages';
import { StatefulSelect } from '@components/StatefulInputs/StatefulSelect/StatefulSelect';
import { useGetSurgeonOptions } from '@utilities/hooks/useGetSurgeonOptions/useGetSurgeonOptions';
import { modifierSideOptions, modifierApproachOptions, anesthesiaTypeOptions } from './useProcedureFormOptions/procedureOptions';
import { StatefulMultiSelect } from '@components/StatefulInputs/StatefulMultiSelect/StatefulMultiSelect';
import { RequestProcedureSchema } from '@utilities/Validation/validationSchema';

const useStyles = makeStyles((theme) =>
	createStyles({
		customSecondarySurgeon: {
			backgroundColor: theme.palette.primary[50],
			border: `1px solid ${theme.palette.primary.main}`,
			padding: theme.spacing(3),
			borderRadius: '4px',
		},
		customSecondarySurgeonInput: {
			'width': '100%',
			'& > div': {
				backgroundColor: theme.palette.common.white,
			},
			'& .MuiFormLabel-root.MuiInputLabel-root': {
				backgroundColor: theme.palette.primary[50],
			},
			'& .MuiFormHelperText-root.Mui-error': {
				backgroundColor: theme.palette.primary[50],
			}
		},
		roboCheckbox: {
			maxWidth: 'fit-content',
		},
		modifiers: {
			'marginTop': '0',
			'gap': '16px',
			'& > div': {
				'flex': '1 1',
				'& > div:first-child > div:first-child > div:first-child > div:first-child': {
					'marginTop': '26px',
					'& ~ label': {
						marginTop: 0,
					},
				},
			},
		},
		equipmentMultiSelect: {
			'& > div:first-child > div:first-child': {
				width: '100%',
			},
		},
		select: {
			'width': '50%',
			'padding': theme.spacing(2, 0, 2, 0),
			'& .MuiInputLabel-root': {
				fontSize: '0.75rem',
				paddingBottom: theme.spacing(0.15),
				color: theme.palette.common.black,
				fontWeight: 550,
			},
			'& .MuiSelect-select > span': {
				fontSize: '1rem',
				color: theme.palette.grey[500],
			},
		},
	}),
);

const ProcedureFormInner = ({ children }: { children: ReactNode }) => {
	const classes = useStyles();
	const { onSubmit, onCancel, shouldDisableSubmit, submitButtonText, headerText } =
		useProcedureFormInfo();
	const { wShowSecondarySurgeon } = useSsmUiFlags();

	const {
		handleSubmit,
		watch,
		formState: { errors, submitCount },
		setValue,
	} = useFormContext<SurgeryProcedureInterface>();
	const isPrimaryProcedure = watch('isPrimaryProcedure');

	const currentValues = watch();
	const initialValues = React.useRef(currentValues);

	const disableSubmit = React.useMemo(
		() =>
			shouldDisableSubmit &&
			shouldDisableSubmit(errors, currentValues, initialValues.current),
		[errors, shouldDisableSubmit, currentValues],
	);

	const formError =
		submitCount > 0 && errors && Object.keys(errors).length > 0
			? 'There are errors in this page. Please review.'
			: undefined;
	const { currentValue: roboticCurrentValue, setCurrentValue: roboticSetCurrentValue } =
		useFieldFormContext<boolean>('robotic');
	const RoboticCheckboxLabel = (
		<p style={{ margin: 0 }}>
			<strong>Yes</strong>, this is a <strong>robotic procedure</strong>
		</p>
	);

	const handleSecondarySurgeonCustomChange = React.useCallback(
		(event: React.ChangeEvent<{ value: string | null }>) => {
			if (currentValues.secondarySurgeon?.isCustom && event.target.value) {
				setValue('customSecondarySurgeon', event.target.value);
				return;
			}
		},
		[currentValues.secondarySurgeon?.isCustom, setValue],
	);

	const surgeonOptions = useGetSurgeonOptions();

	return (
		<>
			<Box mb={8}>
				<Text className="section-title" fontSize={24}>
					{headerText}
				</Text>
			</Box>
			{children}
			<StatefulSelect<SurgeryProcedureInterface>
				name="surgeon"
				label="SURGEON"
				placeholder="Select a surgeon"
				options={surgeonOptions}
				styleAsRequired={true}
				wrapperClassName={classes.select}
				controllerOptions={{
					rules: {
						required: SURGEON_NOT_SELECTED
					}
				}}
				dataField="procedureForm-surgeonSelect"
			/>
			{wShowSecondarySurgeon && Boolean(isPrimaryProcedure) && (
				<>
					<SecondarySurgeonSelect dataField="procedureForm-secondarySurgeonSelect" />
					{currentValues.secondarySurgeon?.isCustom && (
						<Paper
							elevation={0}
							data-field="procedureForm-customSecondarySurgeon"
							className={classes.customSecondarySurgeon}
						>
							<StatefulTextInput
								name="customSecondarySurgeon"
								label="Custom secondary surgeon name"
								dataField="procedureForm-customSecondarySurgeon"
								styleAsRequired={true}
								wrapperClassName={classes.customSecondarySurgeonInput}
								controllerOptions={{
									rules: {
										required: SECONDARY_SURGEON_NAME_REQUIRED,
										onChange: handleSecondarySurgeonCustomChange,
									},
								}}
							/>
						</Paper>
					)}
				</>
			)}
			{Boolean(isPrimaryProcedure) && (
				<FormTextField name="firstAssistant" label="First Assistant" />
			)}
			<CodeChipInput />
			<FormTextField
				name="procedureName"
				label="Procedure name"
				subLabel="Based on CPT code(s) entered"
				placeholder="Type procedure name"
			/>
			<Flex flexDirection="row" mt={8} flexWrap="wrap" className={classes.modifiers}>
				<StatefulSelect<SurgeryProcedureInterface>
					name="modifierSide"
					label="MODIFIER"
					placeholder="Modifier"
					options={modifierSideOptions}
					wrapperClassName={classes.select}
					controllerOptions={{}}
					dataField="procedureForm-modifierSide"
				/>
				<StatefulSelect<SurgeryProcedureInterface>
					name="modifierApproach"
					label="APPROACH"
					placeholder="Approach"
					options={modifierApproachOptions}
					wrapperClassName={classes.select}
					controllerOptions={{}}
					dataField="procedureForm-modifierApproach"
				/>
			</Flex>
			<Box mt={8} data-field="robotic" className={classes.roboCheckbox}>
				<Label>Is this a robotic procedure?</Label>
				<Checkbox
					name="robotic"
					label={RoboticCheckboxLabel}
					checked={roboticCurrentValue}
					onChange={roboticSetCurrentValue}
				/>
			</Box>
			<StatefulMultiSelect<SurgeryProcedureInterface>
				name="anesthesiaType"
				label="ANESTHESIA TYPE"
				placeholder="Select an option"
				options={anesthesiaTypeOptions}
				styleAsRequired={true}
				wrapperClassName={classes.select}
				controllerOptions={{
					rules: { ...RequestProcedureSchema.anesthesiaType },
				}}
				dataField="procedureForm-anesthesiaType"
			/>
			<ImplantsField />
			<Box className={classes.equipmentMultiSelect}>
				<EquipmentMultiSelect />
			</Box>
			<FormTextArea name="otherEquipment" label="Other equipment and supplies" />
			<FormTextArea
				name="additionalComments"
				label="Additional procedure comments"
				mt="-12px"
			/>
			<hr />
			<Flex mt={8} justifyContent="flex-end" alignItems="center">
				<ErrorMessage errorField={formError} />
				<Button secondary onClick={onCancel} className="margin-right-10" id="cancel">
					Cancel
				</Button>
				<Button onClick={handleSubmit(onSubmit)} id="addSurgery" disabled={disableSubmit}>
					{submitButtonText}
				</Button>
			</Flex>
		</>
	);
};

const ProcedureForm = ({ children, ...rest }: ProcedureFormProps) => (
	<FormMetaProvider {...rest}>
		<ProcedureFormInner>{children}</ProcedureFormInner>
	</FormMetaProvider>
);

export default ProcedureForm;
